/**
 * Persuasion Lab API service
 */

import { parseALOsByTypePayload, parseAddALOsResponse } from '@/modules/persuasion-lab/utils/parse-alos-by-type'
import { NodeAxios } from '../api'
import { API_TIMEOUTS } from '@/config/api-timeouts'

export type CppAnchorType = 'company' | 'product' | 'person' | 'group'

/** Decision moment enums for sort/filter. */
export type TimePressure = 'low' | 'medium' | 'high'
export type RiskLevel = 'low' | 'medium' | 'high'
export type IdentityDirection = 'threat' | 'opportunity'

export interface ITargetMeaning {
    targetId: string
    targetName: string
    headline: string
    identity_relevance: 'failure' | 'transition' | 'success' | 'all'
    tension_resolved: string
    proof_anchor: string
    enriched_at: string
    is_manually_edited?: boolean
}
void (null as any as ITargetMeaning)

export interface IGroupMeaning {
    group_key: string
    alo_ids: string[]
    targetId: string
    synthesis: string
    story_thread?: string
    enriched_at: string
}
void (null as any as IGroupMeaning)

/** Response from GET /persuasion-lab/section-specs (blueprint and/or HTML section specs). */
interface SectionSpecsResponse {
    mode: 'blueprint' | 'html' | 'both'
    blueprint?: {
        record_shape: Record<string, string>
        registry: Array<{
            code: string
            label: string
            landing_page_block_type: string
            canonical_copy_slot: string
            section_type_spec: SectionTypeSpecPayload | null
        }>
        block_taxonomy: BlockTaxonomyEntryWithSpec[]
    }
    html?: {
        record_shape: Record<string, string>
        block_taxonomy: BlockTaxonomyEntryWithSpec[]
        section_type_specs: SectionTypeSpecPayload[]
    }
    mapping_note?: string
}
void (null as any as SectionSpecsResponse)

export interface SectionTypeSpecPayload {
    code: string
    name: string
    description: string
    type_labels?: string[]
    tfd: string
    copy_parts: Array<{ slot: string; label: string; min_words?: number; max_words?: number; recommended_words?: number; optional?: boolean; required?: boolean; list_item_constraint?: unknown }>
    image_slots: Array<{ slot: string; max_prompts: number; recommendation?: string }>
}

interface BlockTaxonomyEntryWithSpec {
    id: string
    name: string
    description: string
    tfd: string
    section_type_spec: SectionTypeSpecPayload | null
}
void (null as any as BlockTaxonomyEntryWithSpec)

/** The internal experience of a decision moment: what kind of shift occurs and the dramatic elements that make it tangible. */
export interface StoryMoment {
    moment_type?: 'realization' | 'revelation' | 'transformation'
    /** A visible, tangible thing that makes the shift concrete (max 200 chars). Must NOT be abstract. */
    significant_object?: string
    /** The stable pattern before the trigger disrupted it (max 300 chars). Must NOT be the disruption itself. */
    before_state?: string
    /** Physical location or situation where this moment lands (max 200 chars). Must NOT be thematic. */
    scene?: string
    /** Traceability: which Situation this was derived from. */
    source_situation_id?: string
    /** AI confidence 0–1; null when human-authored. */
    confidence?: number | null
}

/** Decision moment: event-based situation this target faces. id is stable (uuid); trigger required. */
export interface Situation {
    id: string
    slug?: string
    label: string
    trigger: string
    time_pressure?: TimePressure
    risk_level?: RiskLevel
    identity_direction?: IdentityDirection
    notes?: string
    last_validated_at?: string
    story_moment?: StoryMoment | null
}

// ---------------------------------------------------------------------------
// Story Module interfaces
// ---------------------------------------------------------------------------

export type PersuasionMethodCode =
    | 'authority' | 'social_proof' | 'scarcity' | 'reciprocity' | 'consistency'
    | 'liking' | 'loss_aversion' | 'contrast' | 'specificity' | 'mechanism'
    | 'risk_reversal' | 'objection_preemption' | 'status' | 'identity' | 'urgency'
    | 'simplicity' | 'novelty' | 'credibility_stack' | 'commitment' | 'proof_density'
    | 'future_pacing' | 'clarity'

export interface StoryVersions {
    atomic: string
    short: string
    extended: string
    before_state: string
    after_state: string
    transformation: string
    cta: string
    story_logic_map: {
        problem: string
        solution: string
        proof_direction: string
        outcome: string
    }
}

export interface StoryMethodVariantVersion {
    atomic: string
    short: string
    framework_note: string
    image_url: string | null
    sub_method: string | null
    created_at: string
}

export interface StoryMethodVariant {
    method: PersuasionMethodCode
    score_total: number
    score_breakdown: {
        pressure_fit: number
        risk_fit: number
        identity_fit: number
        proof_fit: number
        objection_fit: number
        trust_gap_fit: number
    }
    atomic: string
    short: string
    framework_note: string
    generation_status: 'pending' | 'generating' | 'complete' | 'error'
    /** Image generated specifically for this method's narrative angle. */
    image_url?: string | null
    /** Generation status of this variant's image. */
    image_status?: 'pending' | 'generating' | 'complete' | 'error'
    /** True when variant copy was regenerated after the image was produced — image is out of date. */
    image_stale?: boolean
    /** The sub-method angle used to produce the current version. */
    sub_method?: string | null
    /** Previous versions of this variant, most recent first. */
    versions?: StoryMethodVariantVersion[]
}

export interface StoryImage {
    generation_status: 'pending' | 'generating' | 'complete' | 'error'
    visual_brief_inputs?: { topic: string; outline: string[] } | null
    generated_image_url: string | null
    visual_concept_id: string | null
    /** The Step 0 conceptProse that was actually sent to the image API. */
    concept_prose?: string | null
    /** User-authored directorial note dialled into the Step 0 prompt. */
    user_tension_note?: string | null
}

export interface StoryFramework {
    summary: string
    persuasion_angle: string
    messaging_guardrails: string[]
    identity_anchor: {
        state: 'failure' | 'transition' | 'success'
        label: string
        indicators: string[]
    }
}

export interface Story {
    _id: string
    foundation_run_id: string
    outcome_id: string
    copy_bank_id?: string | null
    target_snapshot_id?: string | null
    story_type: 'company' | 'product' | 'people'
    situation_source: 'inherited' | 'override' | 'none'
    situation_id: string | null
    situation_snapshot: Situation | null
    story_versions: StoryVersions | null
    user_overrides: Partial<Pick<StoryVersions, 'atomic' | 'short' | 'extended'>>
    framework: StoryFramework | null
    active_method: PersuasionMethodCode | null
    method_variants: StoryMethodVariant[]
    story_image: StoryImage
    user_direction_note: string | null
    /** Communication Brief that seeded this story (Foundation or Enhanced). */
    comm_brief_id?: string | null
    /** User edits to the AI-generated strategy framework — persisted without triggering a recompile. */
    framework_overrides?: {
        summary?: string
        persuasion_angle?: string
        messaging_guardrails?: string[]
    } | null
    generation_status: 'pending' | 'generating' | 'complete' | 'error'
    status: 'draft' | 'active' | 'archived'
    createdAt: string
    updatedAt: string
}

/** Goal on run (same shape as campaign goals). */
/**
 * Visual reference image that anchors the central subject across all generated images.
 * Passed as referenceImages[0] to every image generation call under the run.
 */
export interface CharacterAnchor {
    image_url: string
    source: 'user_upload' | 'ai_generated'
    /** Vivid one-paragraph description for text-prompt injection. */
    prompt_anchor: string
    status: 'pending' | 'generating' | 'ready' | 'error'
    generated_at?: string | null
}

export interface PersuasionRunGoal {
    library_goal_id?: string
    name: string
    description?: string
    goal_type?: 'product' | 'company' | 'people'
    is_primary?: boolean
    metrics?: unknown[]
    success_criteria?: unknown[]
    target_audience_context?: string
    messaging_themes?: string[]
}

/**
 * Full shape of a Communication Brief as returned by the API.
 * Generated from a Foundation run's primary claim + value prop.
 * Provides read-only audience context for Situation Profiles (argument_build runs).
 */
export interface ICommunicationBrief {
    _id: string
    name: string
    version_number: number
    is_active_version: boolean
    version_label?: string
    source_claim_text: string
    source_value_prop_text: string
    subject_name: string
    brief_summary: string
    message_core: {
        one_line_headline: string
        plain_english_meaning: string
        core_message: string
        message_promise: string
    }
    audience_alignment: {
        audience_summary: string
        current_problem_state: string[]
        desired_think: string[]
        desired_feel: string[]
        desired_do: string[]
    }
    objection_handling: {
        mapped_objections: Array<{
            text: string
            challenge_type: string
            response_strategy: { what_to_address: string; how_to_frame: string }
        }>
    }
    message_boundaries: {
        avoid_claims: string[]
        avoid_tones: string[]
        avoid_misinterpretations: string[]
    }
    support_context: {
        top_supporting_assets: string[]
        top_liabilities: string[]
        top_omissions: string[]
    }
    asset_instructions: {
        universal_rules: string[]
        ad_guidance: string[]
        landing_page_guidance: string[]
        influencer_guidance: string[]
        social_guidance: string[]
    }
    updated_at: string
    generation_version?: string
    argument_run_id?: string
}

/** Locked argument structure (core claim + pillars) with a user-editable title. Hierarchy: Foundation → Argument (run) → many Outcomes. */
export interface FinalizedOutcome {
    _id: string
    /** Parent Argument (argument_build run) that owns this outcome. */
    runId: string
    /** Parent Foundation (root run). Set at create so we don't have to derive from run chain. */
    foundationRunId?: string
    workspaceId: string
    title: string
    coreClaim: string
    pillars: Array<{ id: string; text: string }>
    contentPieceId: string
    /** When set, points to the Enhanced CommunicationBrief generated from this outcome. */
    commBriefId?: string
    createdAt: string
    updatedAt: string
}

export interface PersuasionRun {
    _id: string
    orgId: string
    workspaceId: string
    ownerId: string
    cpp_anchor_type: CppAnchorType
    cpp_anchor_id: string
    name: string
    status: string
    currentStep: string
    goals?: PersuasionRunGoal[]
    targetSnapshotId?: string
    targetId?: string
    tfdSnapshotId?: string
    tfdId?: string
    aloGroupId?: string
    aloSnapshotId?: string
    /** Run-scoped group for this hypothesis (Argument Builder). Prefer over hypothesisAloGroupId. */
    hypothesisGroupId?: string
    /** Legacy: library group for hypothesis. Use hypothesisGroupId when present. */
    hypothesisAloGroupId?: string
    /** When set, downstream uses only this group's ALOs; when null/undefined, full snapshot (entire list). */
    activeAloGroupId?: string | null
    /** When set, Activate flow uses this finalized outcome (claim + pillars). */
    activeOutcomeId?: string | null
    /** Populated by GET run when activeOutcomeId is set — claim (outcome) for blueprint and Content Editor. */
    activeOutcome?: { _id: string; title: string; coreClaim: string; pillars: Array<{ id: string; text: string }> } | null
    /** When set, this run is a child of the given run (foundation). */
    parentRunId?: string | null
    /** When parentRunId is set: 'argument_build' | 'message_build'. Omit for root foundation runs. */
    runType?: 'argument_build' | 'message_build' | null
    /** When set, this argument_build run was created from this Communication Brief. */
    commBriefId?: string | null
    /** Foundational symbols: identity declaration (name, slogan, etc.). */
    foundationalSymbols?: FoundationalSymbols
    methodologyIds?: string[]
    createdAt: string
    updatedAt: string
    /** Populated by GET run when present */
    targetSnapshot?: {
        name?: string
        description?: string
        profile?: unknown
        situations?: Situation[]
        targetSnapshotVersion?: number
        [k: string]: unknown
    }
    /** Set by list endpoint for card display */
    targetName?: string | null
    /** Identity Engine subject name (company / product / person) from list or GET run */
    cpp_anchor_name?: string | null
    tfdSnapshot?: Record<string, unknown>
    aloSnapshot?: Record<string, unknown>
    /** When activeAloGroupId is set, only that group's ALOs; else same as aloSnapshot. Use this for pillars/copy. */
    effectiveAloSnapshot?: { assets?: unknown[]; liabilities?: unknown[]; omissions?: unknown[] } | null
    /** When run has a hypothesis group, its ALOs (for display on Foundations page). */
    hypothesisGroupALOs?: { assets?: unknown[]; liabilities?: unknown[]; omissions?: unknown[] } | null
    /** Encounter step: hypotheses and primary index. Feeds Claim Structure. */
    encounterSnapshot?: {
        hypothesisList: string[]
        primaryHypothesisIndex: number | null
    }
    /** Situations step: real-world moment and pressure context. Shapes hypotheses, claim, tone downstream. */
    situationSnapshot?: {
        name?: string
        trigger?: string
        constraints?: string
        stakes?: string
        whosWatching?: string
        targetWorries?: string
        think?: string
        feel?: string
        do?: string
    }
    /** Single source of truth for "this argument is for this situation". Points at targetSnapshot.situations[].id. */
    selectedSituationId?: string | null
    /** When run has no selectedSituationId (e.g. Copy Bank), GET run returns parent's; use for Capsule/decision moment display. */
    effectiveSelectedSituationId?: string | null
    /** Activate state: environment, objective, archetype, persuasion methods, blueprint (Option B). */
    activate?: any
    /** One company, many products, many people. When set, getRun returns systemRecord for all. */
    attached_company_id?: string | null
    attached_product_ids?: string[] | null
    attached_people_ids?: string[] | null
    /** Populated by GET run when run is attached to entities. Latest from system — no copy. Use for brand, stories, generation. */
    systemRecord?: {
        entities: {
            company?: Record<string, unknown> & { _source?: 'library_company' }
            products: Array<Record<string, unknown> & { _source?: 'library_product' }>
            people: Array<Record<string, unknown> & { _source?: 'library_person' }>
        }
        stories: Array<Record<string, unknown>>
    } | null
    /** Visual character anchor — reference image for visual continuity across all generated images. */
    character_anchor?: CharacterAnchor | null
}

export interface FoundationalSymbolEntry {
    currentVersion?: string
    strategicMeaning?: string
    notes?: string
}

export interface FoundationalSymbols {
    /** URL of the logo image (upload or media centre pick). Falls back to the linked Brand Asset's primary_logo. */
    logoUrl?: string
    /** What the logo should communicate; why it matters strategically. Annotates the logo stored on the linked Brand Asset. */
    logoStrategicMeaning?: string
    /** Internal notes about the logo (usage, variants, etc.). Annotates the logo stored on the linked Brand Asset. */
    logoNotes?: string
    name?: FoundationalSymbolEntry
    slogan?: FoundationalSymbolEntry
    categoryDescriptor?: FoundationalSymbolEntry
    primaryHeadline?: FoundationalSymbolEntry
    corePositioningLine?: FoundationalSymbolEntry
    brandThesis?: FoundationalSymbolEntry
    valueStatement?: FoundationalSymbolEntry
}

export interface ContentPiece {
    _id: string
    runId: string
    contentType: string
    title?: string
    workingTitle?: string
    status: string
    slotPlanId?: string
    slotPlanVersion?: string
    sectionPlan?: unknown[]
    createdAt: string
    updatedAt: string
}

export const persuasionLabService = {
    async listRuns(options?: {
        rootsOnly?: boolean
        parentRunId?: string
        runType?: 'argument_build' | 'message_build'
        anchorType?: CppAnchorType
        anchorId?: string
    }): Promise<{ runs: PersuasionRun[] }> {
        const params: Record<string, string> = {}
        if (options?.rootsOnly !== undefined) params.rootsOnly = options.rootsOnly ? 'true' : 'false'
        if (options?.parentRunId) params.parentRunId = options.parentRunId
        if (options?.runType) params.runType = options.runType
        if (options?.anchorType) params.anchorType = options.anchorType
        if (options?.anchorId) params.anchorId = options.anchorId
        const res = await NodeAxios.get<{ runs: PersuasionRun[] }>('/persuasion-lab/runs', {
            params: Object.keys(params).length ? params : undefined,
        })
        const data = (res as any).data ?? res
        const runs = Array.isArray(data?.runs) ? data.runs : []
        if (options?.rootsOnly && !options?.parentRunId && runs.length > 0) {
            data.runs = runs.filter((r: PersuasionRun) => !r.parentRunId)
        }
        return data
    },

    async getRun(runId: string): Promise<PersuasionRun & { contentPiece?: ContentPiece }> {
        const res = await NodeAxios.get(`/persuasion-lab/runs/${runId}`)
        return (res as any).data ?? res
    },

    /** Raw encounterSnapshot from DB for auditing (no populates). */
    async getRunEncounterSnapshot(runId: string): Promise<{
        runId: string
        _id: string | null
        parentRunId: string | null
        encounterSnapshot: { hypothesisList?: string[]; primaryHypothesisIndex?: number | null } | null
    }> {
        const res = await NodeAxios.get(`/persuasion-lab/runs/${runId}/encounter-snapshot`)
        return (res as any).data ?? res
    },

    async createRun(body: {
        cpp_anchor_type: CppAnchorType
        cpp_anchor_id: string
        name?: string
        /** When set, this run is a child of the given run (foundation). */
        parentRunId?: string
        /** When parentRunId is set: 'argument_build' for Argument Builder, 'message_build' for Activate. */
        runType?: 'argument_build' | 'message_build'
        /** When set, this argument_build run was created from this Communication Brief. */
        commBriefId?: string
    }): Promise<{
        run: PersuasionRun
        contentPiece: ContentPiece
    }> {
        const res = await NodeAxios.post('/persuasion-lab/runs', body)
        return (res as any).data ?? res
    },

    async updateRun(runId: string, body: Partial<PersuasionRun>): Promise<PersuasionRun> {
        const res = await NodeAxios.patch(`/persuasion-lab/runs/${runId}`, body)
        return (res as any).data ?? res
    },

    async deleteRun(runId: string): Promise<void> {
        await NodeAxios.delete(`/persuasion-lab/runs/${runId}`)
    },

    async getContentPiece(runId: string): Promise<ContentPiece> {
        const res = await NodeAxios.get(`/persuasion-lab/runs/${runId}/content-piece`)
        return (res as any).data ?? res
    },

    async updateContentPiece(runId: string, body: Partial<ContentPiece>): Promise<ContentPiece> {
        const res = await NodeAxios.patch(`/persuasion-lab/runs/${runId}/content-piece`, body)
        return (res as any).data ?? res
    },

    async freezeALOSnapshot(runId: string): Promise<{ aloSnapshot: unknown }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/alo-snapshot`)
        return (res as any).data ?? res
    },

    async updateALOSnapshot(
        runId: string,
        body: { assets?: unknown[]; liabilities?: unknown[]; omissions?: unknown[] }
    ): Promise<{ aloSnapshot: unknown }> {
        const res = await NodeAxios.patch(`/persuasion-lab/runs/${runId}/alo-snapshot`, body)
        return (res as any).data ?? res
    },

    /**
     * Set the hypothesis ALO group (ALOs selected for this hypothesis).
     * Creates or updates a library group. Optionally appends items to the primary snapshot (appendToPrimarySnapshot)
     * and/or the primary library group (appendToPrimaryGroup) so they appear in Foundations main list.
     */
    async setHypothesisALOGroup(
        runId: string,
        body: {
            alos: Array<{ type: 'asset' | 'liability' | 'omission'; category: string; text: string; title?: string; description?: string; [k: string]: unknown }>
            appendToPrimarySnapshot?: boolean
            appendToPrimaryGroup?: boolean
        }
    ): Promise<{ hypothesisGroup: unknown; aloSnapshot?: unknown; primaryGroup?: unknown }> {
        const res = await NodeAxios.put(`/persuasion-lab/runs/${runId}/hypothesis-alo-group`, body)
        return (res as any).data ?? res
    },

    /** List run-scoped Foundations ALO groups (for Choose group / hypothesis). */
    async listFoundationsALOGroups(runId: string): Promise<{ groups: Array<{ _id: string; name: string; description?: string; aloIds: string[]; isHypothesisGroup?: boolean }> }> {
        const res = await NodeAxios.get(`/persuasion-lab/runs/${runId}/foundations-alo-groups`)
        return (res as any).data ?? res
    },

    /** Create a run-scoped Foundations ALO group (e.g. new hypothesis group). */
    async createFoundationsALOGroup(
        runId: string,
        body: { name: string; description?: string; aloIds?: string[]; isHypothesisGroup?: boolean }
    ): Promise<{ group: { _id: string; name: string; description?: string; aloIds: string[]; isHypothesisGroup?: boolean } }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/foundations-alo-groups`, body)
        return (res as any).data ?? res
    },

    /** Update a run-scoped Foundations ALO group (name, description, aloIds). */
    async updateFoundationsALOGroup(
        runId: string,
        groupId: string,
        body: { name?: string; description?: string; aloIds?: string[] }
    ): Promise<{ group: { _id: string; name: string; description?: string; aloIds: string[]; isHypothesisGroup?: boolean } }> {
        const res = await NodeAxios.patch(`/persuasion-lab/runs/${runId}/foundations-alo-groups/${groupId}`, body)
        return (res as any).data ?? res
    },

    /** Delete a run-scoped Foundations ALO group (custom groups only; Hypothesis cannot be deleted). */
    async deleteFoundationsALOGroup(runId: string, groupId: string): Promise<{ success: boolean }> {
        const res = await NodeAxios.delete(`/persuasion-lab/runs/${runId}/foundations-alo-groups/${groupId}`)
        return (res as any).data ?? res
    },

    /** Add ALO IDs to a Foundations ALO group (ALO _ids must exist in the run's snapshot). */
    async addALOsToFoundationsALOGroup(
        runId: string,
        groupId: string,
        aloIds: string[]
    ): Promise<{ group: { _id: string; name: string; description?: string; aloIds: string[]; isHypothesisGroup?: boolean } }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/foundations-alo-groups/${groupId}/alos`, { aloIds })
        return (res as any).data ?? res
    },

    /** Copy the current hypothesis group to the Foundations (parent) run so it appears in Foundations → ALO. */
    async saveFoundationsALOGroupToFoundation(
        runId: string,
        groupId: string
    ): Promise<{ group: { _id: string; name: string; description?: string; aloIds: string[] } }> {
        const res = await NodeAxios.post(
            `/persuasion-lab/runs/${runId}/foundations-alo-groups/${groupId}/save-to-foundation`
        )
        return (res as any).data ?? res
    },

    /** Get profile content (features, benefits, key messages) for import into the ALO inventory. */
    async getRunProfileContent(
        runId: string,
        groupId: string
    ): Promise<{
        profiles: Array<{ id: string; name: string; type: 'company' | 'product' | 'person' }>
        items: Array<{
            key: string
            profileType: 'company' | 'product' | 'person'
            profileId: string
            profileName: string
            field: string
            index: number
            text: string
            suggestedType: 'asset' | 'liability' | 'omission'
            suggestedCategory: string
        }>
        alreadyImportedKeys: string[]
    }> {
        const res = await NodeAxios.get(`/persuasion-lab/runs/${runId}/alo-groups/${groupId}/profile-content`)
        return (res as any).data ?? res
    },

    /** Copy saved library brand truths into an existing foundation ALO group. */
    async loadFoundationALOsFromLibrary(
        runId: string,
        groupId: string,
        libraryGroupId: string
    ): Promise<{ success: boolean; count: number }> {
        const res = await NodeAxios.post(
            `/persuasion-lab/runs/${runId}/alo-groups/${groupId}/load-from-library`,
            { libraryGroupId }
        )
        return (res as any).data ?? res
    },

    /** Copy run ALO snapshot (assets/liabilities/omissions) into an empty brand-truth group. */
    async hydrateFoundationALOGroupFromSnapshot(
        runId: string,
        groupId: string
    ): Promise<{ success: boolean; count: number }> {
        const res = await NodeAxios.post(
            `/persuasion-lab/runs/${runId}/alo-groups/${groupId}/hydrate-from-snapshot`,
            {}
        )
        return (res as any).data ?? res
    },

    /** Batch-import selected profile content items into an ALO group. */
    async importFromProfile(
        runId: string,
        groupId: string,
        items: Array<{
            text: string
            type: 'asset' | 'liability' | 'omission'
            category: string
            sourceProfileRef: { profileType: string; profileId: string; field: string; index: number }
        }>
    ): Promise<{ created: any[]; count: number }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/alo-groups/${groupId}/import-from-profile`, { items })
        return (res as any).data ?? res
    },



    /** Transform content (docs/stories) into draft-ready copy. Modal Contract: POST .../transform-content. */
    async transformContent(
        runId: string,
        body: { content: string; targetUse: string; sourceType?: string; sourceId?: string; insertAs: 'replace_block' | 'new_block'; blockIndex?: number; incorporateContent?: string }
    ): Promise<{ text: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/transform-content`, body)
        return (res as any).data ?? res
    },

    /** Block Lab: generate 3–5 alternative phrasings. */
    async blockAlternatives(runId: string, body: { blockIndex: number; currentText: string; options?: string; incorporateContent?: string }): Promise<{ alternatives: string[] }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/block-alternatives`, body)
        return (res as any).data ?? res
    },

    /** Get options that weave an ALO (asset/liability/omission) into the block. Primary Content Editor use. */
    async blockIncorporateAlo(
        runId: string,
        body: { blockIndex: number; currentText: string; aloContent: string; aloRefs?: string[] }
    ): Promise<{ alternatives: string[] }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/block-incorporate-alo`, body)
        return (res as any).data ?? res
    },

    /** Rewrite block with preset (tighten, expand, simplify, etc.). */
    async blockRewrite(runId: string, body: { blockIndex: number; currentText: string; preset: string }): Promise<{ text: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/block-rewrite`, body)
        return (res as any).data ?? res
    },

    /** Evidence Binder: generate proof line from evidence/ALO refs. */
    async proofLineFromRefs(
        runId: string,
        body: {
            blockIndex?: number
            evidenceRefs?: string[]
            aloRefs?: string[]
            aloContent?: string
            evidenceContent?: string
        }
    ): Promise<{ text: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/proof-line-from-refs`, body)
        return (res as any).data ?? res
    },


    /** Foundation ALO entities (company, products, people) — same shape as Campaigns ALO groups API */
    async getFoundationALOGroups(runId: string): Promise<{ company: any; products: any[]; people: any[] }> {
        const res = await NodeAxios.get(`/persuasion-lab/runs/${runId}/alo-groups`)
        const raw = res as any
        const inner = raw?.data
        // Unwrap: response may be body { success, data: groups } or full axios response (then body is raw.data)
        const groups =
            inner &&
            typeof inner === 'object' &&
            !Array.isArray(inner) &&
            (inner.company !== undefined || Array.isArray(inner.products) || Array.isArray(inner.people))
                ? inner
                : inner?.data != null && typeof inner.data === 'object'
                  ? inner.data
                  : raw
        return groups && typeof groups === 'object'
            ? {
                  company: groups.company ?? null,
                  products: Array.isArray(groups.products) ? groups.products : [],
                  people: Array.isArray(groups.people) ? groups.people : [],
              }
            : { company: null, products: [], people: [] }
    },
    async createFoundationALOGroup(
        runId: string,
        data: {
            level: 'company' | 'product' | 'people'
            assignedToType?: 'product' | 'person'
            assignedToId?: string
            assignedToName?: string
            loadFromLibrary?: boolean
            libraryGroupId?: string
        }
    ): Promise<any> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/alo-groups`, data)
        return ((res as any).data?.data ?? (res as any).data) ?? res
    },
    async updateFoundationALOGroup(
        runId: string,
        groupId: string,
        data: { assignedToName?: string; tags?: string[] }
    ): Promise<any> {
        const res = await NodeAxios.patch(`/persuasion-lab/runs/${runId}/alo-groups/${groupId}`, data)
        return ((res as any).data?.data ?? (res as any).data) ?? res
    },
    async deleteFoundationALOGroup(runId: string, groupId: string): Promise<{ success: boolean; message?: string }> {
        const res = await NodeAxios.delete(`/persuasion-lab/runs/${runId}/alo-groups/${groupId}`)
        return (res as any).data ?? res
    },
    async getFoundationALDsForGroup(
        runId: string,
        groupId: string
    ): Promise<{ attractants: any[]; liabilities: any[]; omissions: any[]; differentiators: any[] }> {
        const res = await NodeAxios.get(`/persuasion-lab/runs/${runId}/alo-groups/${groupId}/alos`)
        const parsed = parseALOsByTypePayload(res)
        return {
            attractants: parsed.attractants,
            liabilities: parsed.liabilities,
            omissions: parsed.omissions,
            differentiators: parsed.omissions,
        }
    },
    async addFoundationALDs(
        runId: string,
        groupId: string,
        alds: Array<{
            type: 'asset' | 'liability' | 'omission'
            category: string
            text: string
            description?: string
            [k: string]: unknown
        }>
    ): Promise<any[]> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/alo-groups/${groupId}/alos`, { alds })
        return parseAddALOsResponse(res)
    },
    async updateFoundationALO(runId: string, aloId: string, data: Record<string, unknown>): Promise<any> {
        const res = await NodeAxios.patch(`/persuasion-lab/runs/${runId}/alos/${aloId}`, data)
        return ((res as any).data?.data ?? (res as any).data) ?? res
    },
    async toggleFoundationALO(runId: string, aloId: string, included: boolean): Promise<any> {
        const res = await NodeAxios.patch(`/persuasion-lab/runs/${runId}/alos/${aloId}/toggle`, { included })
        return ((res as any).data?.data ?? (res as any).data) ?? res
    },
    async deleteFoundationALO(runId: string, aloId: string): Promise<void> {
        await NodeAxios.delete(`/persuasion-lab/runs/${runId}/alos/${aloId}`)
    },

    async copyTargetFromLibrary(runId: string, libraryTargetId: string): Promise<{ targetSnapshot: unknown }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/target-snapshot/copy-from-library`, {
            libraryTargetId,
        })
        return (res as any).data ?? res
    },

    /** Create or replace the run's target snapshot from inline payload (e.g. researched target on Foundations Target page). */
    async createOrReplaceTargetSnapshot(
        runId: string,
        body: { name: string; description?: string; profile?: Record<string, unknown>; research_metadata?: Record<string, unknown> }
    ): Promise<{ targetSnapshot: unknown }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/target-snapshot`, body)
        return (res as any).data ?? res
    },

    async updateTargetSnapshot(runId: string, body: Record<string, unknown>): Promise<{ targetSnapshot: unknown }> {
        const res = await NodeAxios.patch(`/persuasion-lab/runs/${runId}/target-snapshot`, body)
        return (res as any).data ?? res
    },

    /**
     * Suggest 3–7 decision moments from target/run context. User adds selected ones to the snapshot.
     */
    async suggestSituations(
        runId: string,
        options?: { description?: string; additional_context?: string; identity_direction?: string; risk_level?: string; life_contexts?: string[] }
    ): Promise<{ suggestions: Situation[] }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/situations/suggest`, options ?? {}, {
            timeout: API_TIMEOUTS.AI_GENERATION
        })
        return (res as any).data ?? res
    },

    /**
     * Generate a single fully-formed situation from a creative seed string.
     * Does NOT save — returns for user review before committing.
     */
    async generateSituationFromSeed(runId: string, seed: string): Promise<{ ok: boolean; situation: Situation }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/situations/from-seed`, { seed }, {
            timeout: API_TIMEOUTS.AI_GENERATION
        })
        return (res as any).data ?? res
    },

    /**
     * Add a new situation to a run's target snapshot.
     */
    async addSituationToRun(runId: string, situation: Situation): Promise<{ targetSnapshot: unknown }> {
        // Fetch current run to get latest situations + version
        const run = await persuasionLabService.getRun(runId)
        const snap = (run as any)?.targetSnapshot as any
        const currentSituations: Situation[] = Array.isArray(snap?.situations) ? snap.situations : []
        const version = snap?.targetSnapshotVersion ?? 1
        const next = [...currentSituations, situation]
        const res = await NodeAxios.patch(`/persuasion-lab/runs/${runId}/target-snapshot`, {
            situations: next,
            targetSnapshotVersion: version,
        })
        return (res as any).data ?? res
    },

    // ── Character Anchor ──────────────────────────────────────────────────

    /** Upload a user photo as the character anchor. */
    async uploadRunCharacter(runId: string, file: File, promptAnchor?: string): Promise<{ character_anchor: CharacterAnchor }> {
        const form = new FormData()
        form.append('file', file)
        if (promptAnchor) form.append('prompt_anchor', promptAnchor)
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/character`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
        return (res as any).data ?? res
    },

    /** AI-generate a character portrait from persona context. */
    async generateRunCharacter(runId: string): Promise<{ character_anchor: CharacterAnchor; profile: unknown }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/character/generate`)
        return (res as any).data ?? res
    },

    /** Update the prompt_anchor text on an existing character anchor. */
    async updateRunCharacterAnchor(runId: string, promptAnchor: string): Promise<{ character_anchor: CharacterAnchor }> {
        const res = await NodeAxios.patch(`/persuasion-lab/runs/${runId}/character`, { prompt_anchor: promptAnchor })
        return (res as any).data ?? res
    },

    /** Remove the character anchor from the run. */
    async deleteRunCharacter(runId: string): Promise<void> {
        await NodeAxios.delete(`/persuasion-lab/runs/${runId}/character`)
    },

    async copyTFDFromLibrary(runId: string, libraryTFDId: string): Promise<{ tfdSnapshot: unknown }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/tfd-snapshot/copy-from-library`, {
            libraryTFDId,
        })
        return (res as any).data ?? res
    },

    async updateTFDSnapshot(runId: string, body: Record<string, unknown>): Promise<{ tfdSnapshot: unknown }> {
        const res = await NodeAxios.patch(`/persuasion-lab/runs/${runId}/tfd-snapshot`, body)
        return (res as any).data ?? res
    },

    /**
     * Generate initial TFD snapshot from target + goals (no library needed). Creates think/feel/do and sets run.tfdSnapshotId.
     */
    async generateInitialTFD(runId: string): Promise<{ tfdSnapshot: unknown; alreadyExisted?: boolean }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/tfd-snapshot/generate-initial`)
        return (res as any).data ?? res
    },

    /**
     * Generate all 6 TFD attention levels for this run (Foundations). Returns 202; page updates via WebSocket (run:runId, event tfd_generation_progress).
     */
    async generateTFDLevels(runId: string): Promise<{ success: boolean; message?: string; runId?: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/tfd-snapshot/generate-levels`)
        return (res as any).data ?? res
    },

    /**
     * Start an AI query for this run (fire-and-forget). Returns immediately with requestId.
     * Listen on Ably channel run:runId for 'persuasion-ai-done' / 'persuasion-ai-error' with same requestId.
     */
    async aiQuery(runId: string, params: { actionType: string; context: string; prompt: string }): Promise<{ requestId: string; actionType: string; runId: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/ai-query`, params)
        return (res as any).data ?? res
    },

    /** Argument case: thesis + primaryClaim + pillars + pillarEvidence + objections. GET creates if missing. */
    async getOrCreateArgumentCase(runId: string): Promise<{
        thesis?: string
        primaryClaim?: string
        pillars?: Array<{ name: string; justification?: string; order?: number; pillarId?: string }>
        pillarEvidence?: Record<string, Array<{ id: string; title: string; evidence_type: string; strength_tier: string; status: string; excerpt?: string; notes?: string; sourceEvidenceItemId?: string }>>
        objections?: Array<{ title?: string; objection: string; pillarId?: string; claim?: string; rebuttalCandidates?: string[] }>
    }> {
        const res = await NodeAxios.get(`/persuasion-lab/runs/${runId}/argument-case`)
        return (res as any).data ?? res
    },

    async updateArgumentCase(
        runId: string,
        body: {
            thesis?: string
            primaryClaim?: string
            pillars?: Array<{ name: string; justification?: string; order?: number; pillarId?: string }>
            pillarEvidence?: Record<string, Array<{ id: string; title: string; evidence_type: string; strength_tier: string; status: string; excerpt?: string; notes?: string; sourceEvidenceItemId?: string }>>
        }
    ): Promise<unknown> {
        const res = await NodeAxios.patch(`/persuasion-lab/runs/${runId}/argument-case`, body)
        return (res as any).data ?? res
    },

    /** Update objections on the run's argument case (used by Argument Builder → Objections step). */
    async updateArgumentCaseObjections(
        runId: string,
        body: {
            objections: Array<{
                title?: string
                objection: string
                pillarId?: string
                claim?: string
                rebuttalCandidates?: string[]
            }>
        }
    ): Promise<unknown> {
        const res = await NodeAxios.patch(`/persuasion-lab/runs/${runId}/argument-case/objections`, body)
        return (res as any).data ?? res
    },

    /** Finalized outcomes: locked claim structure with title; list per run or all for workspace. */
    async createFinalizedOutcome(
        runId: string,
        body: { title: string; coreClaim: string; pillars: Array<{ id: string; text: string }> }
    ): Promise<FinalizedOutcome> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/finalized-outcomes`, body)
        return (res as any).data ?? res
    },

    async listFinalizedOutcomesByRun(runId: string): Promise<{ outcomes: FinalizedOutcome[] }> {
        const res = await NodeAxios.get(`/persuasion-lab/runs/${runId}/finalized-outcomes`)
        return (res as any).data ?? res
    },

    /** Get one outcome by id (must belong to this run). Used to load outcome into run for editing. */
    async getFinalizedOutcome(runId: string, outcomeId: string): Promise<FinalizedOutcome> {
        const res = await NodeAxios.get(`/persuasion-lab/runs/${runId}/finalized-outcomes/${outcomeId}`)
        return (res as any).data ?? res
    },

    /** Update outcome (e.g. title). */
    async updateFinalizedOutcome(
        runId: string,
        outcomeId: string,
        data: { title?: string; coreClaim?: string; pillars?: Array<{ id: string; text: string }> }
    ): Promise<FinalizedOutcome> {
        const res = await NodeAxios.patch(`/persuasion-lab/runs/${runId}/finalized-outcomes/${outcomeId}`, data)
        return (res as any).data ?? res
    },

    /** Delete outcome. */
    async deleteFinalizedOutcome(runId: string, outcomeId: string): Promise<void> {
        await NodeAxios.delete(`/persuasion-lab/runs/${runId}/finalized-outcomes/${outcomeId}`)
    },

    async listFinalizedOutcomes(): Promise<{ outcomes: FinalizedOutcome[] }> {
        const res = await NodeAxios.get('/persuasion-lab/finalized-outcomes')
        return (res as any).data ?? res
    },

    /** Blueprint section spec and HTML (landing page) section spec. Use mode=blueprint|html or omit for both. */
    async getSectionSpecs(mode?: 'blueprint' | 'html'): Promise<SectionSpecsResponse> {
        const query = mode ? `?mode=${mode}` : ''
        const res = await NodeAxios.get(`/persuasion-lab/section-specs${query}`)
        return (res as any).data ?? res
    },

    async listSlotPlans(params?: { contentType?: string; planId?: string }): Promise<{ plans: { planId: string; version: string; contentType: string; slots: unknown[] }[] }> {
        const query = new URLSearchParams()
        if (params?.contentType) query.append('contentType', params.contentType)
        if (params?.planId) query.append('planId', params.planId)
        const suffix = query.toString() ? `?${query.toString()}` : ''
        const res = await NodeAxios.get(`/persuasion-lab/slot-plans${suffix}`)
        return (res as any).data ?? res
    },


    // ---------------------------------------------------------------------------
    // Story Module
    // ---------------------------------------------------------------------------

    /** Create a new Story and kick off compile pipeline. Returns 202 with storyId. */
    async createStory(
        runId: string,
        body: { story_type: 'company' | 'product' | 'people'; situation_id?: string; outcome_id?: string; user_direction_note?: string; comm_brief_id?: string; story_asset_profile_id?: string }
    ): Promise<{ storyId: string; runId: string; message: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/stories`, body)
        return (res as any).data ?? res
    },

    /** Recompile a story with an optional updated direction note (full compile, all steps). */
    async recompileStory(storyId: string, user_direction_note?: string | null): Promise<{ ok: boolean; message: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/stories/${storyId}/recompile`, { user_direction_note: user_direction_note ?? null })
        return (res as any).data ?? res
    },

    /** List all stories for a run (visual_brief_inputs stripped). */
    async listStories(runId: string): Promise<{ stories: Story[] }> {
        const res = await NodeAxios.get(`/persuasion-lab/runs/${runId}/stories`)
        return (res as any).data ?? res
    },

    /** Get a single story (full document including visual_brief_inputs). */
    async getStory(storyId: string): Promise<Story> {
        const res = await NodeAxios.get(`/persuasion-lab/stories/${storyId}`)
        return (res as any).data ?? res
    },

    /** Update story fields: user_overrides, active_method, story_type, status, user_direction_note. */
    async updateStory(storyId: string, body: Partial<Pick<Story, 'user_overrides' | 'active_method' | 'story_type' | 'status' | 'user_direction_note' | 'framework_overrides'>>): Promise<Story> {
        const res = await NodeAxios.patch(`/persuasion-lab/stories/${storyId}`, body)
        return (res as any).data ?? res
    },

    /** Override the active situation and trigger a scoped recompile. */
    async overrideSituation(storyId: string, body: { situation_id: string }): Promise<{ ok: boolean; message: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/stories/${storyId}/situation-override`, body)
        return (res as any).data ?? res
    },

    /** Restore a historical version of a method variant back to the active slot. */
    async restoreMethodVariantVersion(storyId: string, method: string, versionIndex: number): Promise<{ ok: boolean; story: any }> {
        const res = await NodeAxios.post(`/persuasion-lab/stories/${storyId}/method-variants/${method}/restore/${versionIndex}`)
        return (res as any).data ?? res
    },

    /** Generate a method variant for the specified persuasion method. */
    async generateMethodVariant(storyId: string, method: string, subMethod?: string): Promise<{ ok: boolean; message: string }> {
        const res = await NodeAxios.post(
            `/persuasion-lab/stories/${storyId}/method-variants/${method}`,
            subMethod ? { sub_method: subMethod } : {}
        )
        return (res as any).data ?? res
    },

    /** Trigger story image generation (requires visual_brief_inputs to exist).
     *  When method is provided the image is stored on that method variant; otherwise stored on story_image. */
    async generateStoryImage(storyId: string, method?: string): Promise<{ ok: boolean; message: string }> {
        const params = method ? `?method=${encodeURIComponent(method)}` : ''
        const res = await NodeAxios.post(`/persuasion-lab/stories/${storyId}/generate-image${params}`)
        return (res as any).data ?? res
    },

    /** Save user edits to the visual brief (topic, outline, user_tension_note). */
    async patchVisualBrief(
        storyId: string,
        data: { topic?: string; outline?: string[]; user_tension_note?: string | null }
    ): Promise<{ ok: boolean; story_image?: StoryImage | null }> {
        const res = await NodeAxios.patch(`/persuasion-lab/stories/${storyId}/visual-brief`, data)
        return (res as any).data ?? res
    },

    /** Preview the Step 0 conceptProse without generating an image.
     *  Returns the AI's interpretation of the brief so the user can iterate before generating. */
    async previewVisualConcept(
        storyId: string,
        data: { topic?: string; outline?: string[]; user_tension_note?: string }
    ): Promise<{ ok: boolean; concept_prose: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/stories/${storyId}/visual-brief/preview`, data)
        return (res as any).data ?? res
    },

    /** Generate a fresh visual brief (topic + outline) from a creative seed.
     *  The seed is the primary directive — the AI builds the threshold-moment brief around it.
     *  Returns { topic, outline } without saving; caller applies to editable fields. */
    async generateVisualBriefFromSeed(
        storyId: string,
        creativeSeed: string
    ): Promise<{ ok: boolean; topic: string; outline: string[] }> {
        const res = await NodeAxios.post(`/persuasion-lab/stories/${storyId}/visual-brief/generate`, { creative_seed: creativeSeed })
        return (res as any).data ?? res
    },

    /** Delete a story. */
    async deleteStory(storyId: string): Promise<{ ok: boolean }> {
        const res = await NodeAxios.delete(`/persuasion-lab/stories/${storyId}`)
        return (res as any).data ?? res
    },

    // ---------------------------------------------------------------------------
    // Storyboard Module
    // ---------------------------------------------------------------------------

    /** Create a new storyboard and kick off the compile pipeline. Returns 202 with storyboardId. */
    async createStoryboard(
        storyId: string,
        body: {
            density?: StoryboardDensity
            target_frame_count?: number
            storyboard_intent?: 'paid_ad' | 'influencer_brief' | 'landing_page' | 'social' | 'document'
            story_perspective?: 'customer_journey' | 'brand_as_guide' | 'credibility_witness' | 'transformation' | 'situational' | 'custom'
            central_tension?: string
        }
    ): Promise<{ storyboardId: string; storyId: string; runId: string; message: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/stories/${storyId}/storyboards`, body)
        return (res as any).data ?? res
    },

    /** List all storyboards for a story. */
    async listStoryboards(storyId: string): Promise<{ storyboards: Storyboard[] }> {
        const res = await NodeAxios.get(`/persuasion-lab/stories/${storyId}/storyboards`)
        return (res as any).data ?? res
    },

    /** List all storyboards for the current workspace (for campaign storyboard picker). */
    async listAllStoryboards(filters?: { status?: string }): Promise<{ storyboards: Storyboard[] }> {
        const params = new URLSearchParams()
        if (filters?.status) params.set('status', filters.status)
        const query = params.toString() ? `?${params.toString()}` : ''
        const res = await NodeAxios.get(`/persuasion-lab/storyboards${query}`)
        return (res as any).data ?? res
    },

    /** Get a single storyboard by ID (full document). */
    async getStoryboard(storyboardId: string): Promise<Storyboard> {
        const res = await NodeAxios.get(`/persuasion-lab/storyboards/${storyboardId}`)
        return (res as any).data ?? res
    },

    /** Update storyboard top-level fields. */
    async updateStoryboard(
        storyboardId: string,
        body: Partial<Pick<Storyboard, 'title' | 'density' | 'target_frame_count' | 'status'>>
    ): Promise<Storyboard> {
        const res = await NodeAxios.patch(`/persuasion-lab/storyboards/${storyboardId}`, body)
        return (res as any).data ?? res
    },

    /** Update a single frame's user_overrides or beats. */
    async updateFrame(
        storyboardId: string,
        frameNumber: number,
        body: { user_overrides?: Partial<StoryboardFrame['user_overrides']>; beats?: FrameBeat[]; generated_image_url?: string; generation_status?: string }
    ): Promise<{ ok: boolean; storyboard: Storyboard }> {
        const res = await NodeAxios.patch(`/persuasion-lab/storyboards/${storyboardId}/frames/${frameNumber}`, body)
        return (res as any).data ?? res
    },

    /** Trigger a full storyboard recompile (same density + frame count). */
    async regenerateStoryboard(storyboardId: string): Promise<{ ok: boolean; message: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/storyboards/${storyboardId}/regenerate`)
        return (res as any).data ?? res
    },

    /** Trigger image generation for a specific frame. Requires visual_brief_inputs. */
    async generateFrameImage(storyboardId: string, frameNumber: number): Promise<{ ok: boolean; message: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/storyboards/${storyboardId}/frames/${frameNumber}/image`)
        return (res as any).data ?? res
    },

    /**
     * Regenerate visual brief + image for a single frame.
     * Use this (not generateFrameImage) when the user has edited scene/direction/script
     * so the new text is baked into the image prompt via Step 3 re-run.
     */
    async regenerateFrameImage(storyboardId: string, frameNumber: number): Promise<{ ok: boolean; message: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/storyboards/${storyboardId}/frames/${frameNumber}/regenerate`)
        return (res as any).data ?? res
    },

    /** Upload a local file to replace a frame's generated image. Saves to media library. */
    async uploadFrameImage(storyboardId: string, frameNumber: number, file: File): Promise<{ ok: boolean; generated_image_url: string }> {
        const formData = new FormData()
        formData.append('file', file)
        const res = await NodeAxios.post(
            `/persuasion-lab/storyboards/${storyboardId}/frames/${frameNumber}/upload`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        return (res as any).data ?? res
    },

    /** Queue brand-voice frame script generation for a single frame. */
    async generateFrameScript(storyboardId: string, frameNumber: number): Promise<{ ok: boolean; message: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/storyboards/${storyboardId}/frames/${frameNumber}/script`)
        return (res as any).data ?? res
    },

    /** Fan-out: queue frame script generation for all frames lacking a complete script. */
    async generateAllFrameScripts(storyboardId: string): Promise<{ ok: boolean; queued: number; message: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/storyboards/${storyboardId}/scripts`)
        return (res as any).data ?? res
    },

    /** Delete a storyboard. */
    async deleteStoryboard(storyboardId: string): Promise<{ ok: boolean }> {
        const res = await NodeAxios.delete(`/persuasion-lab/storyboards/${storyboardId}`)
        return (res as any).data ?? res
    },

    /** Enrich a single ALO snapshot item with target meaning. */
    async enrichALOItem(
        runId: string,
        aloId: string,
        body: { targetId?: string }
    ): Promise<{ aloId: string; targetId: string; target_meaning: ITargetMeaning }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/alo-snapshot/${aloId}/enrich`, body)
        return (res as any).data ?? res
    },

    /** Batch-enrich all ALO snapshot items (202; results via Ably persuasion-alo-enriched events). */
    async enrichAllALOs(
        runId: string,
        body: { targetId?: string; force?: boolean }
    ): Promise<{ total: number; message?: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/alo-snapshot/enrich-all`, body)
        return (res as any).data ?? res
    },

    /** Manually update one or more fields on a single target_meaning. Sets is_manually_edited: true. */
    async updateEnrichment(
        runId: string,
        aloId: string,
        body: {
            targetId: string
            headline?: string
            identity_relevance?: 'failure' | 'transition' | 'success' | 'all'
            tension_resolved?: string
            proof_anchor?: string
        }
    ): Promise<{ aloId: string; targetId: string; target_meaning: ITargetMeaning }> {
        const res = await NodeAxios.patch(`/persuasion-lab/runs/${runId}/alo-snapshot/${aloId}/enrich`, body)
        return (res as any).data ?? res
    },

    /** Clear the target_meaning for a specific targetId (or all if omitted). */
    async deleteEnrichment(
        runId: string,
        aloId: string,
        targetId?: string
    ): Promise<{ aloId: string; cleared: boolean }> {
        const params = targetId ? `?targetId=${encodeURIComponent(targetId)}` : ''
        const res = await NodeAxios.delete(`/persuasion-lab/runs/${runId}/alo-snapshot/${aloId}/enrich${params}`)
        return (res as any).data ?? res
    },

    /** Generate group meaning for 2–8 ALO snapshot items. */
    async generateGroupMeaning(
        runId: string,
        body: { aloIds: string[]; targetId?: string }
    ): Promise<{ group_key: string; synthesis: string; story_thread?: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/alo-snapshot/group-meaning`, body)
        return (res as any).data ?? res
    },

    /** ALO Workshop Bot — Socratic SSE chat stream */
    async *chatALOWorkshop(
        runId: string,
        params: {
            messages: Array<{ role: 'user' | 'assistant'; content: string }>
        }
    ): AsyncGenerator<string, void, unknown> {
        const response = await fetch(`/api/persuasion-lab/runs/${runId}/alo-workshop/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(params),
        })
        if (!response.ok || !response.body) {
            throw new Error(`Workshop chat failed: ${response.status}`)
        }
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        while (true) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() ?? ''
            for (const line of lines) {
                const trimmed = line.trim()
                if (!trimmed.startsWith('data:')) continue
                const jsonStr = trimmed.slice(5).trim()
                try {
                    const parsed = JSON.parse(jsonStr)
                    if (parsed.done) return
                    if (parsed.content) yield parsed.content as string
                } catch { /* skip malformed */ }
            }
        }
    },

    /** ALO Workshop Bot — Extract ALOs from conversation and save to group */
    async extractALOsFromWorkshop(
        runId: string,
        params: {
            conversation: Array<{ role: string; content: string }>
            groupId: string
        }
    ): Promise<{ created: any[]; count: number; message?: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/alo-workshop/extract`, params)
        return (res as any).data ?? res
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Creative Context Pack
    // ─────────────────────────────────────────────────────────────────────────

    /** Get or initialize the creative-context pack for a run. */
    async getCreativeContextPack(runId: string): Promise<{ success: boolean; pack: CreativeContextPack }> {
        const res = await NodeAxios.get(`/persuasion-lab/runs/${runId}/creative-context`)
        return (res as any).data ?? res
    },

    /** Update the creative-context pack for a run (partial merge). */
    async updateCreativeContextPack(
        runId: string,
        input: CreativeContextPackInput
    ): Promise<{ success: boolean; pack: CreativeContextPack }> {
        const res = await NodeAxios.put(`/persuasion-lab/runs/${runId}/creative-context`, input)
        return (res as any).data ?? res
    },

    /** Mark the creative-context pack as ready for use in storyboard generation. */
    async markCreativeContextPackReady(runId: string): Promise<{ success: boolean; pack: CreativeContextPack }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/creative-context/ready`)
        return (res as any).data ?? res
    },

    /**
     * AI-generate or refine the creative-context pack.
     * mode: 'generate_full' | 'refine_all' | 'suggest_products' | 'suggest_scenes' | 'suggest_creator'
     * Returns a `suggestion` object the frontend can review and apply.
     */
    async generateCreativeContext(
        runId: string,
        params: {
            direction?: string
            mode: 'generate_full' | 'refine_all' | 'suggest_products' | 'suggest_scenes' | 'suggest_creator'
        }
    ): Promise<{ success: boolean; suggestion: Partial<CreativeContextPack>; mode: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/creative-context/generate`, params)
        return (res as any).data ?? res
    },

    /**
     * AI-generate campaign rules for a reusable profile/template (suggest_creator only).
     */
    async generateCreativeContextForProfile(
        profileId: string,
        params: { direction?: string }
    ): Promise<{ success: boolean; suggestion: Partial<CreativeContextPack>; mode: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/story-assets/profiles/${profileId}/creative-context/generate`, {
            ...params,
            mode: 'suggest_creator',
        })
        return (res as any).data ?? res
    },

    /**
     * Generate a character profile for the pack using AI (casting director call + portrait image).
     */
    async generateCharacterForPack(
        runId: string,
        params: { direction?: string; generate_image?: boolean }
    ): Promise<{ success: boolean; character_context: CharacterContext | null }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/creative-context/character/generate`, params)
        return (res as any).data ?? res
    },

    /**
     * AI-generate a reference image for a scene based on its label and description.
     */
    async generateSceneImage(
        runId: string,
        params: {
            label: string
            description?: string | null
            context_notes?: string | null
            use_case?: string | null
            visual_style_notes?: string | null
        }
    ): Promise<{ success: boolean; url: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/creative-context/scenes/generate-image`, params)
        return (res as any).data ?? res
    },

    /**
     * AI-generate a reference image for a scene on a global template profile.
     */
    async generateSceneImageForProfile(
        profileId: string,
        params: {
            label: string
            description?: string | null
            context_notes?: string | null
            use_case?: string | null
            visual_style_notes?: string | null
        }
    ): Promise<{ success: boolean; url: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/story-assets/profiles/${profileId}/scenes/generate-image`, params)
        return (res as any).data ?? res
    },

    /**
     * AI-generate a character profile for a global template profile (no run context).
     */
    async generateCharacterForProfile(
        profileId: string,
        params: { direction?: string; generate_image?: boolean }
    ): Promise<{ success: boolean; character_context: CharacterContext | null }> {
        const res = await NodeAxios.post(`/persuasion-lab/story-assets/profiles/${profileId}/character/generate`, params)
        return (res as any).data ?? res
    },

    /**
     * Upload a character photo to a global template profile's character_context.
     */
    async uploadCharacterForProfile(profileId: string, file: File): Promise<{ success: boolean; character_context: CharacterContext | null }> {
        const formData = new FormData()
        formData.append('file', file)
        const res = await NodeAxios.post(
            `/persuasion-lab/story-assets/profiles/${profileId}/character/upload`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        return (res as any).data ?? res
    },

    /**
     * Upload a character photo to the pack's character_context.
     */
    async uploadCharacterForPack(runId: string, file: File): Promise<{ success: boolean; character_context: CharacterContext | null }> {
        const formData = new FormData()
        formData.append('file', file)
        const res = await NodeAxios.post(
            `/persuasion-lab/runs/${runId}/creative-context/character/upload`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        return (res as any).data ?? res
    },

    /**
     * Upload an image asset for use in the creative context pack.
     * Returns a CDN URL to store on the relevant pack item (product or scene).
     */
    async uploadCreativeContextAsset(runId: string, file: File): Promise<{ success: boolean; url: string }> {
        const formData = new FormData()
        formData.append('file', file)
        const res = await NodeAxios.post(
            `/persuasion-lab/runs/${runId}/creative-context/assets/upload`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        return (res as any).data ?? res
    },

    /**
     * Fetch images from library entities attached to this run (LibraryProduct + LibraryCompany).
     * Used by the "From library" picker in the Creative Context UI.
     */
    async getRunLibraryImages(
        runId: string
    ): Promise<{ success: boolean; subjects: Array<{ type: 'product' | 'company'; name: string; images: string[] }> }> {
        const res = await NodeAxios.get(`/persuasion-lab/runs/${runId}/creative-context/library-images`)
        return (res as any).data ?? res
    },

    // ─── Story Assets — global template profile library ────────────────────

    /** List all global Story Assets profiles for the org. */
    async listStoryAssetProfiles(): Promise<{ success: boolean; profiles: CreativeContextPack[] }> {
        const res = await NodeAxios.get('/persuasion-lab/story-assets/profiles')
        return (res as any).data ?? res
    },

    /** Create a new standalone Story Assets profile. */
    async createStoryAssetProfile(profile_name: string): Promise<{ success: boolean; profile: CreativeContextPack }> {
        const res = await NodeAxios.post('/persuasion-lab/story-assets/profiles', { profile_name })
        return (res as any).data ?? res
    },

    /** Get a single Story Assets profile by ID. */
    async getStoryAssetProfile(profileId: string): Promise<{ success: boolean; profile: CreativeContextPack }> {
        const res = await NodeAxios.get(`/persuasion-lab/story-assets/profiles/${profileId}`)
        return (res as any).data ?? res
    },

    /** Update a Story Assets profile's content. */
    async updateStoryAssetProfile(profileId: string, input: CreativeContextPackInput): Promise<{ success: boolean; profile: CreativeContextPack }> {
        const res = await NodeAxios.put(`/persuasion-lab/story-assets/profiles/${profileId}`, input)
        return (res as any).data ?? res
    },

    /** Archive (soft-delete) a Story Assets profile. */
    async archiveStoryAssetProfile(profileId: string): Promise<{ success: boolean }> {
        const res = await NodeAxios.delete(`/persuasion-lab/story-assets/profiles/${profileId}`)
        return (res as any).data ?? res
    },

    /** Promote the current run's build pack into a new global template profile. */
    async saveAsStoryAssetProfile(runId: string, profile_name: string): Promise<{ success: boolean; profile: CreativeContextPack }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/creative-context/save-as-profile`, { profile_name })
        return (res as any).data ?? res
    },

    /** Apply a global template profile to the run's build pack. */
    async applyStoryAssetProfile(
        runId: string,
        profileId: string,
        fields?: Array<'character_context' | 'product_context' | 'scene_context' | 'creator_context'>
    ): Promise<{ success: boolean; pack: CreativeContextPack }> {
        const res = await NodeAxios.post(`/persuasion-lab/runs/${runId}/creative-context/apply-profile`, {
            profile_id: profileId,
            fields,
        })
        return (res as any).data ?? res
    },

    // Communication Brief methods
    async generateCommunicationBrief(data: {
        foundation_run_id: string
        primary_claim_id: string
        primary_value_prop_id: string
    }): Promise<{ brief: any }> {
        const response = await NodeAxios.post(
            `/persuasion-lab/foundations/${data.foundation_run_id}/communication-briefs/generate`,
            {
                primary_claim_id: data.primary_claim_id,
                primary_value_prop_id: data.primary_value_prop_id,
            }
        )
        return (response as any).data ?? response
    },

    async listCommunicationBriefs(foundationRunId: string): Promise<{ briefs: ICommunicationBrief[] }> {
        const response = await NodeAxios.get(
            `/persuasion-lab/foundations/${foundationRunId}/communication-briefs`
        )
        return (response as any).data ?? response
    },

    async listWorkspaceCommunicationBriefs(params?: { limit?: number }): Promise<{ briefs: ICommunicationBrief[] }> {
        const limit = params?.limit ?? 24
        const response = await NodeAxios.get(`/persuasion-lab/communication-briefs/workspace`, {
            params: { limit },
        })
        return (response as any).data ?? response
    },

    async getCommunicationBrief(briefId: string): Promise<{ brief: ICommunicationBrief }> {
        const response = await NodeAxios.get(`/persuasion-lab/communication-briefs/${briefId}`)
        return (response as any).data ?? response
    },

    async generateEnhancedBrief(runId: string, outcomeId: string): Promise<{ brief: ICommunicationBrief }> {
        const response = await NodeAxios.post(
            `/persuasion-lab/argument-runs/${runId}/enhanced-briefs/generate`,
            { outcomeId }
        )
        return (response as any).data ?? response
    },

    async updateCommunicationBrief(briefId: string, updates: any): Promise<{ brief: ICommunicationBrief }> {
        const response = await NodeAxios.patch(`/persuasion-lab/communication-briefs/${briefId}`, updates)
        return (response as any).data ?? response
    },

    async deleteCommunicationBrief(briefId: string): Promise<void> {
        await NodeAxios.delete(`/persuasion-lab/communication-briefs/${briefId}`)
    },

    async saveVersion(
        briefId: string,
        data: { version_label?: string; version_notes?: string }
    ): Promise<{ version: any }> {
        const response = await NodeAxios.post(
            `/persuasion-lab/communication-briefs/${briefId}/save-version`,
            data
        )
        return (response as any).data ?? response
    },

    async listVersions(briefId: string): Promise<{ versions: any[] }> {
        const response = await NodeAxios.get(`/persuasion-lab/communication-briefs/${briefId}/versions`)
        return (response as any).data ?? response
    },

    async restoreVersion(briefId: string, versionId: string): Promise<{ brief: any }> {
        const response = await NodeAxios.post(
            `/persuasion-lab/communication-briefs/${briefId}/restore-version/${versionId}`
        )
        return (response as any).data ?? response
    },

    async enhanceSection(
        briefId: string,
        sectionName: string,
        currentContent: any,
        enhancementType: string
    ): Promise<{ options: string[] }> {
        const response = await NodeAxios.post(
            `/persuasion-lab/communication-briefs/${briefId}/enhance-section`,
            {
                section_name: sectionName,
                current_content: currentContent,
                enhancement_type: enhancementType,
            }
        )
        return (response as any).data ?? response
    },

    async getCampaignsUsingBrief(briefId: string): Promise<{ campaigns: any[] }> {
        const response = await NodeAxios.get(
            `/persuasion-lab/communication-briefs/${briefId}/campaigns`
        )
        return (response as any).data ?? response
    },

    // ── Persuasion Board Module ──────────────────────────────────────────────

    async createPersuasionBoards(
        storyId: string,
        body: {
            count: 1 | 3 | 5
            method_override?: string
            comm_brief_id?: string | null
            situation_id?: string | null
        }
    ): Promise<{ boardIds: string[]; batch_id: string; storyId: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/stories/${storyId}/persuasion-boards`, body)
        return (res as any).data ?? res
    },

    async listPersuasionBoards(storyId: string): Promise<{ boards: PersuasionBoard[] }> {
        const res = await NodeAxios.get(`/persuasion-lab/stories/${storyId}/persuasion-boards`)
        return (res as any).data ?? res
    },

    async getPersuasionBoard(boardId: string): Promise<{ board: PersuasionBoard }> {
        const res = await NodeAxios.get(`/persuasion-lab/persuasion-boards/${boardId}`)
        return (res as any).data ?? res
    },

    async updatePersuasionBoard(boardId: string, body: Partial<PersuasionBoard>): Promise<{ board: PersuasionBoard }> {
        const res = await NodeAxios.patch(`/persuasion-lab/persuasion-boards/${boardId}`, body)
        return (res as any).data ?? res
    },

    async deletePersuasionBoard(boardId: string): Promise<{ ok: boolean }> {
        const res = await NodeAxios.delete(`/persuasion-lab/persuasion-boards/${boardId}`)
        return (res as any).data ?? res
    },

    async regeneratePersuasionBoard(boardId: string): Promise<{ ok: boolean }> {
        const res = await NodeAxios.post(`/persuasion-lab/persuasion-boards/${boardId}/regenerate`)
        return (res as any).data ?? res
    },

    async triggerMethodRanking(storyId: string): Promise<{ ok: boolean; message: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/stories/${storyId}/rank-methods`)
        return (res as any).data ?? res
    },

    async triggerBoardCritique(boardId: string): Promise<{ ok: boolean; message: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/persuasion-boards/${boardId}/analyze`)
        return (res as any).data ?? res
    },

    async sharpenEvidence(
        boardId: string,
        body: { field: string; rough_answer: string; current_claim: string }
    ): Promise<{ sharpened: string }> {
        const res = await NodeAxios.post(`/persuasion-lab/persuasion-boards/${boardId}/sharpen`, body, { timeout: 30000 })
        return (res as any).data ?? res
    },

    async enrichBoard(
        boardId: string,
        body: { critique_id: string; field: string; rough_answer: string; sharpened: string; pillar_id: string | null }
    ): Promise<{ board: PersuasionBoard }> {
        const res = await NodeAxios.patch(`/persuasion-lab/persuasion-boards/${boardId}/enrich`, body)
        return (res as any).data ?? res
    },
}

// ---------------------------------------------------------------------------
// Storyboard Module types (exported at module level)
// ---------------------------------------------------------------------------

export type BeatType =
    | 'hook' | 'problem' | 'tension' | 'contrast' | 'reveal'
    | 'proof' | 'transformation' | 'cta' | 'outcome'

export type StoryboardDensity = 'atomic' | 'short' | 'standard' | 'extended' | 'custom'

export interface FrameBeat {
    type: BeatType
    note: string
}

export interface StoryboardFrame {
    frame_number: number
    title: string
    narrative_purpose: string
    beats: FrameBeat[]
    scene_description: string
    visual_direction: string
    script_note: string
    user_overrides: {
        scene_description?: string
        visual_direction?: string
        script_note?: string
    }
    visual_brief_inputs: { topic: string; outline: string[] } | null
    generated_image_url: string | null
    generation_status: 'pending' | 'generating' | 'complete' | 'error'
    frame_script: FrameScript | null
}

export interface FrameScript {
    generation_status: 'pending' | 'generating' | 'complete' | 'error'
    core_message: string
    talking_points: string[]
    social_copy: string[]
    voiceover_draft: string
    user_edited: boolean
    generated_at: string | null
}

export interface StorySnapshot {
    active_density_text: string
    framework: StoryFramework | null
    situation_snapshot: Situation | null
    active_method: PersuasionMethodCode | null
    character_anchor?: CharacterAnchor | null
    comm_brief_id?: string | null
    storyboard_intent?: string | null
    story_perspective?: string | null
    central_tension?: string | null
}

export interface Storyboard {
    _id: string
    story_id: string
    foundation_run_id: string
    comm_brief_id?: string | null
    workspaceId: string
    title: string
    density: StoryboardDensity
    target_frame_count: number
    storyboard_intent?: string | null
    story_perspective?: string | null
    central_tension?: string | null
    frames: StoryboardFrame[]
    generation_status: 'pending' | 'generating' | 'complete' | 'error'
    status: 'draft' | 'active' | 'archived'
    story_snapshot: StorySnapshot
    createdAt: string
    updatedAt: string
}

// ─────────────────────────────────────────────────────────────────────────────
// Creative Context Pack
// ─────────────────────────────────────────────────────────────────────────────

export type CreativeContextPackStatus = 'draft' | 'ready' | 'archived'

export interface ProductLink {
    label: string
    url?: string
    notes?: string
}

export interface MustShowProduct {
    name: string
    sku?: string
    notes?: string
    visual_priority?: 'required' | 'preferred' | 'optional'
    /** CDN URL of a reference image for this product — used in storyboard image generation. */
    reference_image_url?: string
    /** Short image description for prompt injection. */
    image_description?: string
}

export interface CreativeContextScene {
    label: string
    description?: string
    context_notes?: string
    use_case?: string
    /** CDN URL of a reference image for this scene. */
    reference_image_url?: string
    /** Short image description for prompt injection. */
    image_description?: string
}

/** Ambient visual reference — style/environment images not tied to a specific product or scene. */
export interface VisualReference {
    url: string
    label?: string
    description?: string
    kind?: 'product' | 'scene' | 'environment' | 'style'
}

export interface CharacterContext {
    name?: string | null
    image_url?: string | null
    image_source?: 'user_upload' | 'ai_generated' | null
    prompt_anchor?: string | null
    persona_label?: string | null
    physical_description?: string | null
    wardrobe?: string | null
    emotional_register?: string | null
    environment_context?: string | null
    status?: 'pending' | 'generating' | 'ready' | null
}

export interface CreativeContextPack {
    _id: string
    scope: 'template' | 'build'
    profile_name?: string | null
    run_id?: string | null
    story_id?: string | null
    label?: string | null
    product_context: {
        product_links: ProductLink[]
        must_show_products: MustShowProduct[]
        notes?: string | null
    }
    scene_context: {
        scenes: CreativeContextScene[]
        visual_requirements: string[]
        visual_style_notes?: string | null
        visual_references: VisualReference[]
    }
    creator_context: {
        creator_constraints: string[]
        proof_rules: string[]
        disclaimer_rules: string[]
        cta_requirements: string[]
        platform_notes?: string | null
    }
    character_context?: CharacterContext | null
    status: CreativeContextPackStatus
    createdAt: string
    updatedAt: string
}

export type CreativeContextPackInput = Partial<{
    label: string
    profile_name: string
    product_context: Partial<CreativeContextPack['product_context']>
    scene_context: Partial<CreativeContextPack['scene_context']>
    creator_context: Partial<CreativeContextPack['creator_context']>
    character_context: Partial<CharacterContext> | null
    status: CreativeContextPackStatus
}>

// ─────────────────────────────────────────────────────────────────────────────
// Communication Brief (Foundation-level strategic asset)
// ─────────────────────────────────────────────────────────────────────────────
// Methods are now part of persuasionLabService object above

// ─────────────────────────────────────────────────────────────────────────────
// Persuasion Board Module types
// ─────────────────────────────────────────────────────────────────────────────

export interface PersuasionBoard {
    _id: string
    story_id: string
    foundation_run_id: string
    org: string
    owner: string
    persuasion_method: PersuasionMethodCode
    rank: number
    comm_brief_id?: string | null
    situation_id?: string | null
    situation_snapshot?: any | null
    batch_id: string
    board_summary?: string | null
    method_rationale?: string | null
    narrative_arc?: { before: string; pivot: string; after: string } | null
    identity_anchor?: { state: 'failure' | 'transition' | 'success'; label: string; indicators: string[] } | null
    cta?: string | null
    execution_translation?: string | null
    message_bullets?: string[]
    hero_image: {
        generation_status: 'pending' | 'generating' | 'complete' | 'error'
        visual_brief_inputs?: { topic: string; outline: string[] } | null
        generated_image_url?: string | null
        concept_prose?: string | null
        visual_concept_id?: string | null
    }
    narrative_perspective_mode?: 'interior_threat' | 'interior_aspiration' | 'credibility_witness' | null
    visual_execution_mode?: 'identity_dominant' | 'evidence_dominant' | 'gap_dominant' | 'social_dominant' | 'mechanism_dominant' | null
    generation_status: 'pending' | 'generating' | 'complete' | 'error'
    status: 'draft' | 'active' | 'archived'
    critique?: {
        strength_score: number
        critiques: Array<{
            critique_id: string
            field: string
            issue: string
            prompt: string
            example: string
            severity: 'high' | 'medium' | 'low'
            pillar_id: string | null
        }>
        strengths: string[]
        session_count: number
        generated_at: string
    } | null
    enrichments?: Array<{
        critique_id: string
        field: string
        rough_answer: string
        sharpened: string
        pillar_id: string | null
        added_at: string
    }>
    createdAt: string
    updatedAt: string
}
